import { FC } from 'react';
import { useWatch } from 'react-hook-form';

import { AlertDialogWrapper } from '../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { FormInput, Input } from '../../../../components/input/index';
import { useReactForm } from '../../../../hooks/index';
import { DataRoomModalType } from '../../enums/data-room-modal-type.enum';
import { DataRoomModalStore } from '../../store/data-room-modal.store';
import { useRenameVaultFile } from '../hooks/use-rename-vault-file.hook';
import {
  RenameVaultFileForm,
  renameVaultFileSchema,
} from '../validations/rename-vault-file.validation';

const FILE_LABEL = <AppFormattedMessage id="FILE" />;

// A Vault file's rename, looking as it does on the Vault page. It closes once the rename is saved:
// a mutation's callbacks do not run after its component has gone.
export const RenameVaultFileModal: FC = () => {
  const isOpen = DataRoomModalStore(({ isOpen }) => isOpen);
  const type = DataRoomModalStore(({ type }) => type);
  const metadata = DataRoomModalStore(({ metadata }) => metadata);
  const handleAccept = DataRoomModalStore(({ handleAccept }) => handleAccept);
  const handleDismiss = DataRoomModalStore(({ handleDismiss }) => handleDismiss);
  const { isPending, renameFile } = useRenameVaultFile({ onSuccess: handleAccept });
  const { control } = useReactForm({ schema: renameVaultFileSchema });
  const { name } = useWatch<RenameVaultFileForm>({ control });

  const file = metadata?.vaultFile;

  if (type !== DataRoomModalType.RENAME_VAULT_FILE || !file) {
    return null;
  }

  const isValid = renameVaultFileSchema.safeParse({ name }).success;

  const save = () => {
    if (!name) return;

    renameFile({ fileId: file.id, folderId: file.folderId, link: file.link, name });
  };

  return (
    <AlertDialogWrapper
      control={{ onOpenChange: (open) => !open && handleDismiss(), open: isOpen }}
    >
      <div className="m-2 flex w-full max-w-[493px] flex-col gap-8 rounded bg-white p-4">
        <div className="flex flex-col gap-4">
          <span className="text-xl font-semibold text-gray-700">
            {FILE_LABEL} <AppFormattedMessage id="RENAME" />
          </span>
          <Input
            disabled
            onChange={() => ''}
            placeholder={
              <AppFormattedMessage id="CURRENT_FILE_NAME" values={{ text: FILE_LABEL }} />
            }
            value={file.name}
            wrapperClassName="transition-all duration-1000 w-full"
          />
          <FormInput
            control={control}
            name="name"
            placeholder={<AppFormattedMessage id="NEW_FILE_NAME" values={{ text: FILE_LABEL }} />}
            wrapperClassName="transition-all duration-1000 w-full"
          />
        </div>
        <div className="flex h-11 w-full justify-end gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-300 px-6 py-[10px] text-sm font-[450] text-gray-700"
            onClick={handleDismiss}
            styleType="NONE"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <Button
            className="h-full w-fit bg-brand-700 px-6 py-[10px] text-sm font-[550] text-white"
            disabled={!isValid || isPending}
            isLoading={isPending}
            onClick={save}
            styleType="NONE"
          >
            <AppFormattedMessage id="DONE" />
          </Button>
        </div>
      </div>
    </AlertDialogWrapper>
  );
};
